/**
 * Os documentos que a empresa mandou para assinatura eletrônica — de qualquer módulo que use o
 * motor (entrega de EPI hoje; CIPA e ASO depois). Daqui se acompanha quem já assinou e se abre a
 * trilha de evidências de cada um.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { DataTable, PageShell, type DataTableColumn } from '../../components';
import assinaturaService from '../../services/assinaturaService';
import {
  COR_DO_DOCUMENTO,
  ROTULO_DO_DOCUMENTO,
  STATUS_DO_DOCUMENTO,
  rotuloDaOrigem,
  type DocumentoParaAssinatura,
  type StatusDoDocumento,
} from '../../types/assinatura';

const colunas: DataTableColumn<DocumentoParaAssinatura>[] = [
  { id: 'titulo', label: 'Documento', primary: true, render: (d) => d.titulo },
  { id: 'origem', label: 'Origem', hideOnMobile: true, render: (d) => rotuloDaOrigem(d.origemTipo) },
  {
    id: 'signatarios',
    label: 'Assinaturas',
    render: (d) => {
      const assinaram = d.signatarios.filter((s) => s.status === 'assinado').length;
      return (
        <Typography variant="body2" component="span">
          {assinaram} de {d.signatarios.length}
        </Typography>
      );
    },
  },
  {
    id: 'status',
    label: 'Situação',
    render: (d) => <Chip size="small" color={COR_DO_DOCUMENTO[d.status]} label={ROTULO_DO_DOCUMENTO[d.status]} />,
  },
  { id: 'createdAt', label: 'Enviado em', hideOnMobile: true, render: (d) => new Date(d.createdAt).toLocaleDateString('pt-BR') },
  {
    id: 'prazo',
    label: 'Prazo',
    hideOnMobile: true,
    render: (d) => (d.status === 'pendente' ? new Date(d.expiraEm).toLocaleDateString('pt-BR') : '—'),
  },
];

export const AssinaturaListPage: React.FC = () => {
  const navigate = useNavigate();

  const [documentos, setDocumentos] = useState<DocumentoParaAssinatura[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(20);
  const [filtro, setFiltro] = useState<StatusDoDocumento | ''>('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resultado = await assinaturaService.listar({
        ...(filtro ? { status: filtro } : {}),
        pagina: pagina + 1,
        limite: porPagina,
      });
      setDocumentos(resultado.documentos);
      setTotal(resultado.total);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar os documentos.');
    } finally {
      setCarregando(false);
    }
  }, [filtro, pagina, porPagina]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Assinaturas' }]}
      title="Assinaturas"
      subtitle="Documentos enviados para assinatura eletrônica e a situação de cada signatário"
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      <FormControl size="small" sx={{ mb: 2, minWidth: 220 }}>
        <InputLabel id="filtro-assinatura">Situação</InputLabel>
        <Select
          labelId="filtro-assinatura"
          label="Situação"
          value={filtro}
          onChange={(e) => {
            setFiltro(e.target.value as StatusDoDocumento | '');
            setPagina(0);
          }}
        >
          <MenuItem value="">Todas</MenuItem>
          {STATUS_DO_DOCUMENTO.map((s) => (
            <MenuItem key={s} value={s}>
              {ROTULO_DO_DOCUMENTO[s]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DataTable
        columns={colunas}
        rows={documentos}
        getRowId={(d) => d.id}
        loading={carregando}
        onRowClick={(d) => navigate(`/assinaturas/${d.id}`)}
        filtersActive={filtro !== ''}
        onClearFilters={() => setFiltro('')}
        pagination={{
          page: pagina,
          rowsPerPage: porPagina,
          totalCount: total,
          onPageChange: setPagina,
          onRowsPerPageChange: (n) => {
            setPorPagina(n);
            setPagina(0);
          },
        }}
        emptyState={{
          title: 'Nenhum documento enviado para assinatura nesta empresa',
          description:
            'Os documentos chegam aqui quando algum módulo pede assinatura eletrônica, como a ficha de entrega de EPI. Cada um mostra quem já assinou e a trilha de evidências.',
        }}
      />
    </PageShell>
  );
};

export default AssinaturaListPage;
